// ============================================================================
// 📁 File: WorkerListPage.jsx
// 🧭 Module: PPE → Resources → Worker Master
// 📝 Pattern:
//   - SAME AS Tool / Skill Master
//   - KPI cards on top
//   - Drawer-based Create/Edit
//   - Search + Status filter
//   - Auto reload (no manual refresh)
// ============================================================================

import React, { useEffect, useState } from "react";
import { Card, Button, Input, Select, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import axiosClient from "../../../../../api/axiosClient";

import WorkerDrawer from "./WorkerDrawer";
import WorkerKpiBar from "../../../components/worker/WorkerKpiBar";

// ============================================================================
// COMPONENT
// ============================================================================

const WorkerListPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  // --------------------------------------------------------------------------
  // LOADERS
  // --------------------------------------------------------------------------

  const loadWorkers = async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get("/api/ppe/masters/workers");
      setRows(res.data?.data || []);
    } catch {
      message.error("Failed to load workers");
    } finally {
      setLoading(false);
    }
  };

  const reloadAll = async () => {
    await loadWorkers();
  };

  // --------------------------------------------------------------------------
  // LIFECYCLE
  // --------------------------------------------------------------------------

  useEffect(() => {
    reloadAll();
  }, []);

  // --------------------------------------------------------------------------
  // ACTIONS
  // --------------------------------------------------------------------------

  const handleCreate = () => {
    setEditingRecord(null);
    setDrawerOpen(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setEditingRecord(null);
  };

  const toggleActive = async (record, isActive) => {
    try {
      await axiosClient.put(`/api/ppe/masters/workers/${record.id}`, {
        is_active: isActive,
      });
      message.success(
        `Worker ${isActive ? "activated" : "deactivated"}`
      );
      reloadAll();
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Status update failed"
      );
    }
  };

  // --------------------------------------------------------------------------
  // FILTERING
  // --------------------------------------------------------------------------

  const filteredRows = rows.filter((r) => {
    const q = search.toLowerCase();

    const matchesSearch =
      !q ||
      r.worker_code?.toLowerCase().includes(q) ||
      r.worker_name?.toLowerCase().includes(q);

    const matchesStatus =
      !statusFilter || r.status_code === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------

  return (
    <>
      {/* HEADER */}
      <div
        style={{
          background:
            "linear-gradient(90deg, #0ea5e9 0%, #38bdf8 50%, #22d3ee 100%)",
          borderRadius: 12,
          padding: "16px 20px",
          color: "#fff",
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h4 style={{ margin: 0 }}>Worker Master</h4>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            Workforce & skill allocation
          </div>
        </div>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          Create Worker
        </Button>
      </div>

      {/* KPI BAR */}
      <WorkerKpiBar rows={rows} />

      {/* FILTER BAR */}
      <Card style={{ marginBottom: 12 }}>
        <div className="d-flex gap-3 flex-wrap">
          <Input
            placeholder="Search by worker code or name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 260 }}
            allowClear
          />

          <Select
            placeholder="Status"
            allowClear
            style={{ width: 180 }}
            value={statusFilter || undefined}
            onChange={setStatusFilter}
          >
            <Select.Option value="DRAFT">Draft</Select.Option>
            <Select.Option value="ACTIVE">Active</Select.Option>
            <Select.Option value="INACTIVE">Inactive</Select.Option>
          </Select>
        </div>
      </Card>

      {/* TABLE */}
      <Card>
        <div className="card-body p-0">
          <table className="table table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th>Worker Code</th>
                <th>Worker Name</th>
                <th>Skill</th>
                <th>Department</th>
                <th>Status</th>
                <th style={{ width: 220 }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="text-center">
                    Loading...
                  </td>
                </tr>
              )}

              {!loading && filteredRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    No workers found
                  </td>
                </tr>
              )}

              {filteredRows.map((r) => (
                <tr key={r.id}>
                  <td>{r.worker_code}</td>
                  <td>{r.worker_name}</td>
                  <td>{r.skill_name || "-"}</td>
                  <td>{r.department_name || "-"}</td>

                  <td>
                    <span
                      className={`badge ${
                        r.status_code === "DRAFT"
                          ? "bg-warning"
                          : r.status_code === "ACTIVE"
                          ? "bg-success"
                          : r.status_code === "INACTIVE"
                          ? "bg-secondary"
                          : "bg-light"
                      }`}
                    >
                      {r.status_code || "-"}
                    </span>
                  </td>

                  <td>
                    <div className="d-flex gap-2 flex-wrap">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleEdit(r)}
                      >
                        Edit
                      </button>

                      {r.status_code !== "ACTIVE" && (
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => toggleActive(r, true)}
                        >
                          Activate
                        </button>
                      )}

                      {r.status_code === "ACTIVE" && (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => toggleActive(r, false)}
                        >
                          Deactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* DRAWER */}
      <WorkerDrawer
        open={drawerOpen}
        record={editingRecord}
        onClose={handleDrawerClose}
        onSuccess={reloadAll}
      />
    </>
  );
};

export default WorkerListPage;
